import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, BadgeCheck, GraduationCap, Briefcase,
  Languages, IndianRupee, Stethoscope, Building2, Hospital, MapPin, Phone, Mail, Clock, ExternalLink, Sparkles,
} from "lucide-react";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function DoctorProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [doc, setDoc] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [clinics, setClinics] = useState([]);
  const isPremiumPatient = user?.role === "patient" && user?.subscribed;

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/public/doctors/${id}`).then((r) => setDoc(r.data.doctor)).catch(() => setNotFound(true));
  }, [id]);

  useEffect(() => {
    if (isPremiumPatient) {
      api.get(`/patient/doctors/${id}/clinics`).then((r) => setClinics(r.data.clinics || [])).catch(() => setClinics([]));
    } else {
      setClinics([]);
    }
  }, [id, isPremiumPatient]);

  if (notFound)
    return <section className="pgwrap"><p className="pg-lead">Doctor not found. <Link to="/specialties">Browse specialties</Link></p></section>;
  if (!doc)
    return <section className="pgwrap"><p className="pg-lead">Loading…</p></section>;

  const facts = [
    doc.qualifications && { icon: GraduationCap, label: "Qualifications", value: doc.qualifications },
    doc.experience_years && { icon: Briefcase, label: "Experience", value: doc.experience_years },
    doc.languages && { icon: Languages, label: "Languages", value: doc.languages },
    doc.consult_fee && { icon: IndianRupee, label: "Consultation fee", value: doc.consult_fee },
  ].filter(Boolean);

  return (
    <section className="pgwrap" data-testid="doctor-profile">
      <button className="sp-back" onClick={() => nav(-1)} data-testid="dp-back">
        <ArrowLeft size={15} /> Back
      </button>

      <div className="dp-head">
        <div className="dp-photo">
          {doc.photo_id ? <img src={`${API}/public/image/${doc.photo_id}`} alt={doc.name} />
            : <Stethoscope size={44} />}
        </div>
        <div className="dp-meta">
          <span className="pg-eyebrow dp-verified">Verified specialist <BadgeCheck size={14} /></span>
          <h2 className="pg-h2" data-testid="dp-name">{doc.name}</h2>
          <p className="dp-spec">{doc.specialty}{doc.reg_no ? ` · ${doc.reg_no}` : ""}</p>
          {doc.bookable === false ? (
            <div className="banner info" style={{ marginTop: 14 }} data-testid="dp-booking-soon">
              Booking opens on <b>{doc.opens_at_label || "shortly"}</b>. This specialist is setting up their profile — you can book a consultation once it opens.
            </div>
          ) : (
            <button className="pg-btn pg-btn-green pg-btn-lg" style={{ marginTop: 14 }}
              onClick={() => nav(`/book?specialty=${encodeURIComponent(doc.specialty || "")}&doctor=${doc.id}`)}
              data-testid="dp-book">
              Book consultation <ArrowRight size={16} />
            </button>
          )}
        </div>
      </div>

      {facts.length > 0 && (
        <div className="dp-facts" data-testid="dp-facts">
          {facts.map((f, i) => (
            <div className="dp-fact" key={i}>
              <f.icon size={18} />
              <div><span>{f.label}</span><strong>{f.value}</strong></div>
            </div>
          ))}
        </div>
      )}

      {doc.bio && (
        <div className="sp-card dp-bio-card">
          <div className="sp-card-h"><Stethoscope size={18} /><h3>About {doc.name}</h3></div>
          <p className="dp-bio">{doc.bio}</p>
        </div>
      )}

      {isPremiumPatient && clinics.length > 0 && (
        <div className="dp-estabs" data-testid="dp-establishments">
          <div className="dp-estabs-head">
            <span className="pg-eyebrow" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Sparkles size={13} /> Premium · In-person establishments</span>
            <h3 className="pg-h3" style={{ margin: "6px 0 0" }}>Visit {doc.name} in person</h3>
          </div>
          <div className="dp-estab-grid">
            {clinics.map((c) => (
              <div className="dp-estab-card" key={c.id} data-testid={`dp-estab-${c.id}`}>
                <div className="dp-estab-top">
                  <span className="estab-icon">{c.establishment_type === "Hospital" ? <Hospital size={18} /> : <Building2 size={18} />}</span>
                  <div>
                    <h4>{c.name}</h4>
                    <span className="estab-type-chip">{c.establishment_type}</span>
                  </div>
                </div>
                {(c.photos || []).length > 0 && (
                  <div className="dp-estab-photos">
                    {c.photos.slice(0, 4).map((fid) => <img key={fid} src={`${API}/public/image/${fid}`} alt={c.name} />)}
                  </div>
                )}
                <div className="dp-estab-facts">
                  {c.address && <span><MapPin size={13} /> {c.address}{c.pincode ? ` · ${c.pincode}` : ""}</span>}
                  {c.phone && <span><Phone size={13} /> {c.phone}</span>}
                  {c.email && <span><Mail size={13} /> {c.email}</span>}
                  {c.hours && <span><Clock size={13} /> {c.hours}</span>}
                  {c.maps_url && <a href={c.maps_url} target="_blank" rel="noreferrer"><ExternalLink size={13} /> Open in Google Maps</a>}
                </div>
                {Object.keys(c.slots || {}).filter(day => (c.slots[day] || []).length).length > 0 && (
                  <div className="dp-estab-slots">
                    <div className="dp-estab-slots-label"><Clock size={12} /> Available slots</div>
                    {Object.keys(c.slots).filter(day => (c.slots[day] || []).length).map(day => (
                      <div className="dp-estab-slot-row" key={day}><b>{day}</b><span>{(c.slots[day] || []).join(", ")}</span></div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {facts.length === 0 && !doc.bio && (
        <p className="sp-hint" style={{ marginTop: 22 }}>Full profile details for this specialist are being added soon.</p>
      )}
    </section>
  );
}
